"use client";

import { useState, useRef, useCallback, memo, useMemo } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { STATIC_BRANDS, getBrandHref } from "@/lib/navigation/brand";

const PANEL_CLASS =
  "!rounded-[20px] overflow-hidden border border-white/10 shadow-2xl bg-[#1D1E20]/40 backdrop-blur-[22px]";

const TEXT_LINK =
  "!text-mute hover:!text-white !font-normal transition-colors";

const BrandItem = memo(({ brand, onNavigate }) => {
  const href = getBrandHref(brand);
  if (!href) return null;

  return (
    <Link
      href={href}
      onClick={onNavigate}
      className={`block px-3 py-2.5 rounded-xl text-sm ${TEXT_LINK}`}
    >
      {brand.title || brand.name}
    </Link>
  );
});

BrandItem.displayName = "BrandItem";

const BrandMenu = ({ initialBrands = STATIC_BRANDS }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(closeMenu, 120);
  }, [closeMenu]);

  const activeBrands = useMemo(
    () => initialBrands.filter((brand) => brand.id),
    [initialBrands]
  );

  return (
    <div className="relative" onMouseLeave={handleMouseLeave}>
      <Link
        href="/brands"
        onMouseEnter={handleMouseEnter}
        onClick={closeMenu}
        className={`group flex items-center gap-x-5 cursor-pointer py-2 ${TEXT_LINK}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-sm font-medium">برندها</span>
        <ChevronDown
          size={16}
          className={`text-mute group-hover:text-white transition-all duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </Link>

      {isOpen && (
        <div
          onMouseEnter={handleMouseEnter}
          className="absolute top-full right-0 mt-1 z-[9999] animate-in fade-in slide-in-from-top-1 duration-200"
        >
          <div className={`${PANEL_CLASS} max-h-[440px]`}>
            <div className="max-h-[430px] overflow-y-auto custom-scrollbar-dark p-2">
              {activeBrands.length > 0 ? (
                activeBrands.map((brand) => (
                  <BrandItem
                    key={brand.id}
                    brand={brand}
                    onNavigate={closeMenu}
                  />
                ))
              ) : (
                <div className="p-8 text-center text-mute text-sm">
                  برندی یافت نشد
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BrandMenu);
